<script setup lang="ts">
import type { Sidebar } from '@/types/global.types'
import Icon from '@/components/shared/Icon.vue'
import { hasAccess } from '@/lib'
import { getThemeSettings } from '@/lib'
import { useBreakpoints, useEventListener, useStorage } from '@vueuse/core'
import { computed, onMounted, ref, watch, type CSSProperties } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps<{
  visible: boolean
}>()

const {
  sidebar,
  logoHref = '/',
  logo,
  logoMargin = '0px',
  appTitle,
  IdentityPanelHost,
  sidebarLocation,
} = getThemeSettings()

const expandedItems = ref<Set<string>>(new Set())
const fullPath = ref<string>('')
const hasMounted = ref(false)

const sidebarWidth = useStorage('global-sidebar-width', 260)
const sidebarAnchor = ref<HTMLElement | null>(null)
const dragAnchor = ref<HTMLElement | null>(null)

const isResizing = ref(false)
const startX = ref(0)
const startWidth = ref(0)

const route = useRoute()
const router = useRouter()

const isSidebarRight = sidebarLocation === 'right'
const breakpoints = useBreakpoints({ lg: 1024 })
const isDesktop = breakpoints.greaterOrEqual('lg')

const minSidebarWidth = 260
const maxSidebarWidth = 400

const sidebarLinks = [
  {
    Type: 'UrlSidebarLink',
    Name: 'Dashboard',
    Label: 'Dashboard',
    Icon: 'home',
    Link: '/',
    Audiences: ['Role: Everyone'],
  } as Sidebar,
  ...sidebar,
]

const logoHrefElement = computed(() => (isRelativeUrl(logoHref) ? 'router-link' : 'a'))

const sidebarStyles = computed<CSSProperties>(() => {
  const widthValue = `${sidebarWidth.value}px`
  const base: CSSProperties = {
    opacity: props.visible ? '1' : '0',
    pointerEvents: props.visible ? 'auto' : 'none',
  }

  if (isDesktop.value) {
    const offset = isSidebarRight ? '12px' : '-12px'
    base.width = props.visible ? widthValue : '0px'
    base.transform = props.visible ? 'translate3d(0, 0, 0)' : `translate3d(${offset}, 0, 0)`
  } else {
    const offscreen = isSidebarRight ? '104%' : '-104%'
    base.width = widthValue
    base.maxWidth = `min(90vw, ${maxSidebarWidth}px)`
    base.transform = props.visible
      ? 'translate3d(0, 0, 0)'
      : `translate3d(${offscreen}, 0, 0)`
  }

  return base
})

function isRelativeUrl(url: string): boolean {
  return /^\/(?!\/)/.test(url)
}

function removeBasePath(url: string): string {
  const formIndex = url.indexOf('/form/')
  return formIndex !== -1 ? url.substring(formIndex) : url
}

function findParentByLink(root: Sidebar[]): Sidebar | null {
  function search(children: Sidebar[], parent: Sidebar | null): Sidebar | null {
    for (const child of children) {
      if (child.Link === route.path || child.Link === window.location.href) return parent

      if (child.Children) {
        const found = search(child.Children, child)
        if (found) return found
      }
    }
    return null
  }

  return search(root, null)
}

function handleClick(item: Sidebar, event: MouseEvent) {
  if (item.Children) {
    expandedItems.value.has(item.Label)
      ? expandedItems.value.delete(item.Label)
      : expandedItems.value.add(item.Label)
  } else {
    const isNewTabClick = event.metaKey || event.ctrlKey

    if (isNewTabClick) {
      // Open in new tab for command/ctrl+click
      if (!isRelativeUrl(item.Link) && !item.Link.includes('form')) {
        window.open(item.Link, '_blank')
      } else {
        const link = isRelativeUrl(item.Link) ? item.Link : removeBasePath(item.Link)
        const fullUrl = new URL(link, window.location.origin).href
        window.open(fullUrl, '_blank')
      }
    } else {
      // Normal click behavior
      if (!isRelativeUrl(item.Link) && !item.Link.includes('form')) {
        window.location.href = item.Link
        return
      }

      const link = isRelativeUrl(item.Link) ? item.Link : removeBasePath(item.Link)
      router.push(link)
    }
  }
}

watch(route, () => {
  fullPath.value = window.location.href

  if (!hasMounted.value) {
    hasMounted.value = true
    const item = findParentByLink(sidebar)

    if (item?.Label && !expandedItems.value.size) expandedItems.value.add(item.Label)
  }
})

function startResizing(e: MouseEvent) {
  if (!dragAnchor.value?.contains(e.target as Node)) return

  isResizing.value = true
  startX.value = e.clientX
  startWidth.value = sidebarWidth.value

  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'

  // Prevent text selection during resize
  e.preventDefault()
}

function handleTouchStart(e: TouchEvent) {
  if (!dragAnchor.value?.contains(e.target as Node)) return

  isResizing.value = true
  startX.value = e.touches[0].clientX
  startWidth.value = sidebarWidth.value

  document.body.style.userSelect = 'none'
  e.preventDefault()
}

function handleTouchMove(e: TouchEvent) {
  if (!isResizing.value) return

  const delta = e.touches[0].clientX - startX.value
  let newWidth = Math.min(Math.max(startWidth.value + delta, minSidebarWidth), maxSidebarWidth)

  // Add snapping to default width when close
  const defaultWidth = 260
  const snapThreshold = 15
  if (Math.abs(newWidth - defaultWidth) < snapThreshold) {
    newWidth = defaultWidth
  }

  sidebarWidth.value = newWidth
  document.body.style.setProperty('--sidebar-width', `${newWidth}px`)

  // Add visual feedback
  if (sidebarAnchor.value) {
    sidebarAnchor.value.style.boxShadow = '4px 0 12px rgba(0,0,0,0.15)'
  }

  e.preventDefault()
}

function handleKeyResize(e: KeyboardEvent) {
  const step = 20
  let newWidth = sidebarWidth.value

  if (e.key === 'ArrowLeft') {
    newWidth = Math.max(sidebarWidth.value - step, minSidebarWidth)
    e.preventDefault()
  } else if (e.key === 'ArrowRight') {
    newWidth = Math.min(sidebarWidth.value + step, maxSidebarWidth)
    e.preventDefault()
  } else if (e.key === 'Home') {
    newWidth = minSidebarWidth
    e.preventDefault()
  } else if (e.key === 'End') {
    newWidth = maxSidebarWidth
    e.preventDefault()
  } else if (e.key === 'Enter' || e.key === ' ') {
    newWidth = 260 // Reset to default
    e.preventDefault()
  }

  if (newWidth !== sidebarWidth.value) {
    sidebarWidth.value = newWidth
    document.body.style.setProperty('--sidebar-width', `${newWidth}px`)
  }
}

function stopResizing() {
  if (!isResizing.value) return

  isResizing.value = false
  document.body.style.cursor = ''
  document.body.style.userSelect = ''

  // Remove visual feedback
  if (sidebarAnchor.value) {
    sidebarAnchor.value.style.boxShadow = ''
  }
}

function resize(e: MouseEvent) {
  if (!isResizing.value) return

  const delta = e.clientX - startX.value
  let newWidth = Math.min(Math.max(startWidth.value + delta, minSidebarWidth), maxSidebarWidth)

  // Add snapping to default width when close
  const defaultWidth = 260
  const snapThreshold = 15
  if (Math.abs(newWidth - defaultWidth) < snapThreshold) {
    newWidth = defaultWidth
  }

  sidebarWidth.value = newWidth
  document.body.style.setProperty('--sidebar-width', `${newWidth}px`)

  // Add visual feedback
  if (sidebarAnchor.value) {
    sidebarAnchor.value.style.boxShadow = isResizing.value ? '4px 0 12px rgba(0,0,0,0.15)' : ''
  }
}

// Mouse events
useEventListener(document, 'mousedown', startResizing, { passive: false })
useEventListener(document, 'mouseup', stopResizing)
useEventListener(document, 'mousemove', resize)
useEventListener(document, 'mouseleave', stopResizing)

// Touch events for mobile support
useEventListener(document, 'touchstart', handleTouchStart, { passive: false })
useEventListener(document, 'touchend', stopResizing)
useEventListener(document, 'touchmove', handleTouchMove)
useEventListener(document, 'touchcancel', stopResizing)

onMounted(() => {
  document.body.style.setProperty('--sidebar-width', `${sidebarWidth.value}px`)
})
</script>

<template>
  <aside
    ref="sidebarAnchor"
    class="sidebar-shell flex overflow-hidden absolute top-[60px] bottom-0 z-20 flex-col h-full select-none primary-gradient dark:bg-gray-800 dark:border-gray-700 dark:border-r shrink-0 lg:relative lg:z-0 lg:top-auto lg:bottom-auto"
    :class="{
      'sidebar-shell--resizing': isResizing,
      'sidebar-shell--right': isSidebarRight,
      'sidebar-shell--left': !isSidebarRight,
    }"
    :style="sidebarStyles"
    :aria-hidden="!visible"
  >
    <div
      v-if="visible"
      ref="dragAnchor"
      class="absolute inset-y-0 right-0 z-10 w-6 opacity-0 transition-all duration-200 transform translate-x-1/2 hover:opacity-100 cursor-col-resize group"
      :class="{ 'opacity-100': isResizing }"
      role="separator"
      aria-label="Resize sidebar"
      tabindex="0"
      @keydown="handleKeyResize"
    >
      <div
        class="w-[2px] left-1/2 transform -translate-x-1/2 bg-accent-500/60 group-hover:bg-accent-500 group-hover:w-[3px] absolute inset-y-0 transition-all duration-200"
      />
    </div>
    <nav class="flex flex-col flex-1 min-h-0 h-full">
      <div class="flex-1 overflow-y-auto min-h-0">
        <component
          :is="logoHrefElement"
          :to="logoHref"
          :href="logoHref"
          class="flex gap-x-3.5 items-center px-6 pt-6 pb-5"
          tabindex="-1"
        >
          <img
            class="h-8 w-auto max-w-[212px]"
            :src="logo"
            alt="Company Logo"
            :style="{ margin: logoMargin }"
          />
          <h1 v-if="appTitle" class="text-xl font-medium text-white">
            {{ appTitle }}
          </h1>
        </component>
        <ul class="px-3">
          <li v-for="item in sidebarLinks" :key="item.Label">
            <button
              type="button"
              class="flex gap-x-3 justify-between items-center px-4 py-1.5 w-full text-sm font-medium text-white truncate rounded-md hover:bg-white/10 focus:ring-inset focus:ring-2 focus:ring-white/50 focus:outline-none"
              :class="{
                'bg-white/20 hover:bg-white/20': route.path === item.Link || fullPath === item.Link,
              }"
              @click="(event) => handleClick(item, event)"
            >
              <div class="flex gap-x-3 items-center">
                <Icon
                  v-if="item.Icon"
                  :variant="item.Icon"
                  class="w-5 h-5 text-white/90 shrink-0"
                />
                <span>{{ item.Label }}</span>
              </div>
              <Icon
                v-if="item.Children"
                variant="chevron-down"
                class="w-5 h-5 text-white/90 shrink-0"
              />
            </button>

            <ul
              v-if="item.Children"
              class="overflow-hidden"
              :style="{ height: expandedItems.has(item.Label) ? 'auto' : '0px' }"
            >
              <li v-for="child in item.Children" :key="child.Label">
                <button
                  type="button"
                  class="flex gap-x-3 items-center py-1.5 pr-4 pl-7 w-full text-sm font-medium text-white truncate rounded-md hover:bg-white/10 focus:ring-inset focus:ring-2 focus:ring-white/50 focus:outline-none"
                  :class="{
                    'bg-white/20 hover:bg-white/20':
                      route.path === child.Link || fullPath === child.Link,
                  }"
                  :tabindex="expandedItems.has(item.Label) ? 0 : -1"
                  @click="(event) => handleClick(child, event)"
                >
                  <div class="flex gap-x-3 items-center">
                    <Icon
                      v-if="child.Icon"
                      :variant="child.Icon"
                      class="w-5 h-5 text-white/90 shrink-0"
                    />
                    <span>{{ child.Label }}</span>
                  </div>
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      <div
        v-if="hasAccess('Settings')"
        class="flex flex-col gap-y-2 px-6 py-4 border-t border-white/20 shrink-0"
      >
        <a
          class="flex justify-center items-center py-1.5 pr-4 pl-7 w-full text-sm font-medium text-white truncate rounded-md border hover:bg-white/10 focus:ring-inset focus:ring-2 focus:ring-white/50 focus:outline-none border-white/20"
          :href="`https://${IdentityPanelHost}/settings/servicepanel`"
        >
          <Icon variant="settings" class="w-[18px] h-[18px] shrink-0 mr-2" />
          <span>Settings</span>
        </a>
      </div>
    </nav>
  </aside>
</template>

<style scoped>
.sidebar-shell {
  will-change: width, transform, opacity;
  transition-property: width, transform, opacity;
  transition-duration: 360ms;
  transition-timing-function: cubic-bezier(0.32, 0.72, 0, 1);
}

.sidebar-shell--resizing {
  transition-duration: 0ms !important;
}

.sidebar-shell--left {
  left: 0;
  right: auto;
}

@media (max-width: 1023px) {
  .sidebar-shell {
    transition-property: transform, opacity;
  }

  .sidebar-shell--right {
    left: auto;
    right: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-shell {
    transition-duration: 0.01ms !important;
    transition-delay: 0ms !important;
  }
}
</style>
